<template>
  <div
    ref="cellRef"
    data-header-cell="true"
    :data-row="row"
    :data-col="col"
    :data-row-span="rowSpan && rowSpan > 1 ? rowSpan : undefined"
    :data-col-span="colSpan && colSpan > 1 ? colSpan : undefined"
    :data-copy-value="copyValue"
    :class="cn(BASE_CLASS, props.class)"
  >
    <div class="flex min-w-0 items-center">
      <PivotRevealer
        v-if="revealed"
        :opened
        kind="column"
        class="-my-0.5"
        @click="handleToggle"
        @mousedown.stop
      />
      <DSTooltip v-if="tooltip">
        <DSTooltipTrigger as-child>
          <div class="cursor-help truncate text-sm leading-none underline decoration-dotted decoration-muted-foreground underline-offset-2 sm:text-xs">
            <span data-text="true">{{ text }}</span>
            <div
              v-if="desc"
              class="mt-0.5 truncate text-[11px] leading-tight text-muted-foreground/50 font-normal"
            >
              {{ desc }}
            </div>
          </div>
        </DSTooltipTrigger>
        <DSTooltipContent>{{ tooltip }}</DSTooltipContent>
      </DSTooltip>
      <div
        v-else
        class="truncate text-sm leading-none sm:text-xs"
      >
        <span data-text="true">{{ text }}</span>
        <div
          v-if="desc"
          class="mt-0.5 truncate text-[11px] leading-tight text-muted-foreground/50 font-normal"
        >
          {{ desc }}
        </div>
      </div>
    </div>
    <DSTooltip v-if="allowResize">
      <DSTooltipTrigger as-child>
        <div
          ref="resizerRef"
          data-resizer="true"
          class="absolute top-0 right-0 bottom-0 z-30 w-1.5 cursor-col-resize flex items-center justify-center [&:hover_div]:bg-primary [&:hover_div]:h-[90%]"
          @mousedown.stop
        >
          <div class="w-px h-4.5 bg-border/75 transition-all duration-100" />
        </div>
      </DSTooltipTrigger>
      <DSTooltipContent>Drag to resize · double-click to reset</DSTooltipContent>
    </DSTooltip>
  </div>
</template>

<script setup lang="ts">
import { HTMLAttributes, computed, useTemplateRef } from 'vue'

import useResizeService from '@/components/hooks/resize'
import { cn } from '@/helpers/cn'

import { DSTooltip, DSTooltipContent, DSTooltipTrigger } from '@ds'

import PivotRevealer from './PivotRevealer.vue'

// Shared with `PivotFooterCell` by hand, not a constant — they diverge in sticky-edge specifics.
// No `data-cell` here (unlike `PivotFooterCell`/`PivotValueCell`) — `usePivotSelection` uses
// `[data-cell]` as the arrow-key/Tab navigable extent, which excludes headers. Selection itself
// still addresses every cell via `data-row`/`data-col` below, so a header's CSS-grid `colSpan`
// merging needs no special casing there.
const BASE_CLASS = 'font-ds text-foreground bg-card z-10 min-h-5 sticky top-0 grid items-center px-4 py-2.75 cursor-cell'

type Props = {
  text: string
  desc?: string
  /** Explains what the metric means, on hover. Falsy renders no tooltip. */
  tooltip?: string
  allowResize?: boolean
  class?: HTMLAttributes['class']
  /** Whether this column-group level can be collapsed — undefined for the deepest level/grand total. */
  revealed?: boolean
  /** Whether the group this cell heads is currently expanded. */
  opened?: boolean
  /** Negative — header rows count up to -1 immediately above the body. See `usePivotSelection`. */
  row: number
  /** 0-based leaf-column position, left to right. */
  col: number
  /** How many header rows (levels) this cell spans — omitted/1 for an unmerged cell. */
  rowSpan?: number
  /** How many leaf columns this cell spans — omitted/1 for an unmerged cell. */
  colSpan?: number
}

type Emits = {
  resize: [width: number]
  reset: []
  toggle: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const copyValue = computed(() => [props.text, props.desc].filter(Boolean).join(' '))

const cellRef = useTemplateRef('cellRef')
const resizerRef = useTemplateRef('resizerRef')

if (props.allowResize) {
  useResizeService(
    cellRef,
    resizerRef,
    width => emit('resize', width),
    () => emit('reset'),
  )
}

const handleToggle = (event: MouseEvent) => {
  event.stopPropagation()
  event.preventDefault()
  emit('toggle')
}
</script>
